import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControlLabel,
  Grid2,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Navbar from './Navbar';
import HeadingText from '../../BuiltInComponent/HeadingTest';
import {
  DEFAULT_POS_ORDER_TYPE_SETTINGS,
  fetchPosOrderTypeSettings,
  savePosOrderTypeSettings,
} from '../../utils/posOrderTypeSettings';

const OPTIONS = [
  { key: 'enableDineIn', title: 'Dine In', description: 'Table orders aur dine-in billing enable rakhein.' },
  { key: 'enableTakeaway', title: 'Takeaway', description: 'Counter takeaway orders enable rakhein.' },
  { key: 'enableDelivery', title: 'Delivery', description: 'Regular customer delivery orders enable rakhein.' },
  { key: 'enableFoodpanda', title: 'Foodpanda', description: 'Foodpanda aggregator orders enable rakhein.' },
];
const MODE_TO_SETTING = { dinein: 'enableDineIn', takeaway: 'enableTakeaway', delivery: 'enableDelivery' };

const BUSINESS_PROFILES = [
  { value: 'restaurant', label: 'Restaurant', disabled: false },
  { value: 'bakery', label: 'Bakery', disabled: false },
  { value: 'supermart', label: 'Super Mart (Future)', disabled: true },
  { value: 'medical_store', label: 'Medical Store (Future)', disabled: true },
];

export default function POSOrderTypeSettings() {
  const { user } = useSelector((state) => state.auth);
  const [settings, setSettings] = useState(DEFAULT_POS_ORDER_TYPE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [printerAssignments, setPrinterAssignments] = useState([]);
  const [printerForm, setPrinterForm] = useState({ userID: '', printerName: '', printerIP: '', printerPort: 9100, printerPaperMm: 80, autoPrint: true });
  const activeBranchID = user?.defaultBranchID || user?.branchID || null;

  const loadPrinterAssignments = async () => {
    if (!activeBranchID) return;
    const [usersResponse, assignmentsResponse] = await Promise.all([
      axios.post(`${global.SET_API_URL}users/getAllUsers`, { cmpID: user?.cmpID || 1, branchID: activeBranchID, includeAll: true }),
      axios.get(`${global.SET_API_URL}posOrderTypeSettings/forward-printers`, { params: { branchID: activeBranchID } }),
    ]);
    setUsers(usersResponse.data || []);
    setPrinterAssignments(assignmentsResponse.data?.data || []);
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await fetchPosOrderTypeSettings();
        setSettings(data);
      } catch (error) {
        setMessage('Settings load nahi ho sakin. Defaults use ho rahe hain.');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  useEffect(() => { loadPrinterAssignments().catch(() => setMessage('User printer assignments load nahi ho sakin.')); }, [activeBranchID]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePrinterAssignment = async () => {
    if (!printerForm.userID || !printerForm.printerIP.trim()) return setMessage('User aur Printer IP select karein.');
    setSaving(true);
    try {
      await axios.post(`${global.SET_API_URL}posOrderTypeSettings/forward-printers`, { ...printerForm, branchID: activeBranchID });
      await loadPrinterAssignments();
      setPrinterForm({ userID: '', printerName: '', printerIP: '', printerPort: 9100, printerPaperMm: 80, autoPrint: true });
      setMessage('User ka Forward Invoice printer save ho gaya.');
    } catch (error) { setMessage(error?.response?.data?.message || 'Printer assignment save nahi hui.'); }
    finally { setSaving(false); }
  };

  const editPrinterAssignment = (row) => setPrinterForm({ userID: row.userID, printerName: row.printerName || '', printerIP: row.printerIP || '', printerPort: Number(row.printerPort) || 9100, printerPaperMm: Number(row.printerPaperMm) === 58 ? 58 : 80, autoPrint: Boolean(row.autoPrint) });
  const removePrinterAssignment = async (id) => {
    await axios.delete(`${global.SET_API_URL}posOrderTypeSettings/forward-printers/${id}`);
    await loadPrinterAssignments();
    setMessage('User printer assignment remove ho gayi.');
  };
  const testAssignedPrinter = async (row = printerForm) => {
    if (!row.printerIP) return setMessage('Printer IP enter karein.');
    try {
      await axios.post(`${global.SET_API_URL}printerAssignment/testPrinter`, { printerIP: row.printerIP, printerPort: row.printerPort || 9100, categoryDesciption: 'Bakery Forward Invoice' });
      setMessage(`Printer ${row.printerIP} network par reachable hai.`);
    } catch (error) { setMessage(error?.response?.data?.message || 'Printer test failed.'); }
  };

  const enabledCount = useMemo(
    () => OPTIONS.filter((option) => Boolean(settings[option.key])).length,
    [settings]
  );
  const displayedOptions = useMemo(() => {
    const ordered = (settings.restaurantOrderModeOrder || ['dinein', 'takeaway', 'delivery'])
      .map((mode) => OPTIONS.find((option) => option.key === MODE_TO_SETTING[mode])).filter(Boolean);
    return [...ordered, OPTIONS.find((option) => option.key === 'enableFoodpanda')];
  }, [settings.restaurantOrderModeOrder]);

  const moveOrderMode = (mode, direction) => setSettings((prev) => {
    const order = [...(prev.restaurantOrderModeOrder || ['dinein', 'takeaway', 'delivery'])];
    const index = order.indexOf(mode), target = index + direction;
    if (index < 0 || target < 0 || target >= order.length) return prev;
    [order[index], order[target]] = [order[target], order[index]];
    return { ...prev, restaurantOrderModeOrder: order };
  });

  const handleToggle = (key) => (_event, checked) => {
    setSettings((prev) => ({ ...prev, [key]: checked }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await savePosOrderTypeSettings(settings);
      setSettings(data);
      setMessage('POS business profile aur order type settings save ho gayi hain.');
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Settings save nahi ho sakin.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestForwardPrinter = async () => {
    if (!settings.forwardInvoicePrinterIP) {
      setMessage('Printer IP enter karein.');
      return;
    }
    try {
      await axios.post(`${global.SET_API_URL}printerAssignment/testPrinter`, {
        printerIP: settings.forwardInvoicePrinterIP,
        printerPort: settings.forwardInvoicePrinterPort,
        categoryDesciption: 'Bakery Forward Invoice',
      });
      setMessage('Forward Invoice printer network par reachable hai.');
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Printer test failed.');
    }
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ py: 3 }}>
        <HeadingText text="POS Order Type Settings" />

        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6" fontWeight={700}>
                Business Profile
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Restaurant current POS ko unchanged rakhta hai. Bakery profile Bakery POS aur forward-invoice workflow ke liye foundation hai.
              </Typography>
              <TextField
                select
                fullWidth
                label="POS Use For"
                value={settings.businessProfile}
                onChange={(event) => setSettings((prev) => ({ ...prev, businessProfile: event.target.value }))}
                disabled={loading || saving}
              >
                {BUSINESS_PROFILES.map((profile) => (
                  <MenuItem key={profile.value} value={profile.value} disabled={profile.disabled}>
                    {profile.label}
                  </MenuItem>
                ))}
              </TextField>

              {settings.businessProfile === 'bakery' && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(settings.enableForwardInvoices)}
                        onChange={handleToggle('enableForwardInvoices')}
                        disabled={loading || saving}
                      />
                    )}
                    label={<Typography fontWeight={700}>Enable Forward Invoice Workflow</Typography>}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Salesman unpaid forward invoice banayega; cashier scan karke payment receive karega. Cashier direct sale ka option bhi Bakery POS mein rahega.
                  </Typography>
                  <Typography fontWeight={700} sx={{ mt: 2, mb: 1 }}>
                    Forward Invoice Network Printer
                  </Typography>
                  <Grid2 container spacing={1.5}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth size="small" label="Printer Name"
                        value={settings.forwardInvoicePrinterName}
                        onChange={(event) => setSettings((prev) => ({ ...prev, forwardInvoicePrinterName: event.target.value }))}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth size="small" label="Printer IP *"
                        placeholder="192.168.1.100"
                        value={settings.forwardInvoicePrinterIP}
                        onChange={(event) => setSettings((prev) => ({ ...prev, forwardInvoicePrinterIP: event.target.value }))}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth size="small" type="number" label="Printer Port"
                        value={settings.forwardInvoicePrinterPort}
                        onChange={(event) => setSettings((prev) => ({ ...prev, forwardInvoicePrinterPort: Number(event.target.value) || 9100 }))}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        select fullWidth size="small" label="Paper Width"
                        value={settings.forwardInvoicePrinterPaperMm}
                        onChange={(event) => setSettings((prev) => ({ ...prev, forwardInvoicePrinterPaperMm: Number(event.target.value) }))}
                      >
                        <MenuItem value={80}>80 mm</MenuItem>
                        <MenuItem value={58}>58 mm</MenuItem>
                      </TextField>
                    </Grid2>
                  </Grid2>
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={(
                      <Switch
                        checked={Boolean(settings.autoPrintForwardInvoice)}
                        onChange={handleToggle('autoPrintForwardInvoice')}
                        disabled={loading || saving}
                      />
                    )}
                    label="Forward invoice create hote hi automatically print karein"
                  />
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={handleTestForwardPrinter}
                      disabled={loading || saving || !settings.forwardInvoicePrinterIP}
                    >
                      Test Printer Connection
                    </Button>
                  </Box>
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography fontWeight={800}>User-wise Forward Invoice Printers</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      User assignment ko priority milegi. Assignment na ho to upar wala default printer use hoga.
                    </Typography>
                    <Grid2 container spacing={1.25}>
                      <Grid2 size={{ xs: 12, sm: 6 }}><TextField select fullWidth size="small" label="User" value={printerForm.userID} onChange={(e) => setPrinterForm((p) => ({ ...p, userID: e.target.value }))}>{users.map((row) => <MenuItem key={row.userID} value={row.userID}>{row.dispName} ({row.userName})</MenuItem>)}</TextField></Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Printer Name" value={printerForm.printerName} onChange={(e) => setPrinterForm((p) => ({ ...p, printerName: e.target.value }))} /></Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Printer IP *" value={printerForm.printerIP} onChange={(e) => setPrinterForm((p) => ({ ...p, printerIP: e.target.value }))} /></Grid2>
                      <Grid2 size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="number" label="Port" value={printerForm.printerPort} onChange={(e) => setPrinterForm((p) => ({ ...p, printerPort: Number(e.target.value) || 9100 }))} /></Grid2>
                      <Grid2 size={{ xs: 6, sm: 3 }}><TextField select fullWidth size="small" label="Paper" value={printerForm.printerPaperMm} onChange={(e) => setPrinterForm((p) => ({ ...p, printerPaperMm: Number(e.target.value) }))}><MenuItem value={80}>80 mm</MenuItem><MenuItem value={58}>58 mm</MenuItem></TextField></Grid2>
                    </Grid2>
                    <FormControlLabel control={<Switch checked={printerForm.autoPrint} onChange={(_e, checked) => setPrinterForm((p) => ({ ...p, autoPrint: checked }))} />} label="Auto print for this user" />
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}><Button variant="contained" onClick={savePrinterAssignment} disabled={saving}>Save User Printer</Button><Button variant="outlined" onClick={() => testAssignedPrinter()}>Test</Button></Stack>
                    <Stack spacing={1}>{printerAssignments.map((row) => <Box key={row.assignmentID} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}><Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}><Box><Typography fontWeight={700}>{row.dispName} ({row.userName})</Typography><Typography variant="body2">{row.printerName || 'Forward Printer'} — {row.printerIP}:{row.printerPort} — {row.printerPaperMm}mm {row.autoPrint ? '— Auto' : ''}</Typography></Box><Stack direction="row" spacing={1}><Button size="small" onClick={() => testAssignedPrinter(row)}>Test</Button><Button size="small" onClick={() => editPrinterAssignment(row)}>Edit</Button><Button size="small" color="error" onClick={() => removePrinterAssignment(row.assignmentID)}>Remove</Button></Stack></Stack></Box>)}</Stack>
                  </Box>
                </Box>
              )}

              <Typography variant="h6" fontWeight={700} sx={{ pt: 1 }}>
                Restaurant Order Modes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                POS screen par jo order modes istemal karne hain unhein enable rakhein. Agar sab off kar dein to system Takeaway ko fallback ke tor par on rakhega.
              </Typography>

              <Grid2 container spacing={2}>
                {displayedOptions.map((option) => {
                  const mode = Object.keys(MODE_TO_SETTING).find((key) => MODE_TO_SETTING[key] === option.key);
                  const modeIndex = mode ? settings.restaurantOrderModeOrder.indexOf(mode) : -1;
                  return (
                  <Grid2 size={{ xs: 12, sm: 6 }} key={option.key}>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={Boolean(settings[option.key])}
                            onChange={handleToggle(option.key)}
                            disabled={loading || saving}
                          />
                        )}
                        label={<Typography fontWeight={700}>{option.title}</Typography>}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                      {mode && <Stack direction="row" spacing={1} sx={{ mt: 1 }}><Button size="small" variant="outlined" disabled={modeIndex === 0} onClick={() => moveOrderMode(mode, -1)}>Move Up</Button><Button size="small" variant="outlined" disabled={modeIndex === 2} onClick={() => moveOrderMode(mode, 1)}>Move Down</Button></Stack>}
                    </Box>
                  </Grid2>
                  );
                })}
              </Grid2>
              <Box><Button size="small" onClick={() => setSettings((prev) => ({ ...prev, restaurantOrderModeOrder: ['dinein', 'takeaway', 'delivery'] }))}>Reset Order to Default</Button></Box>

              <Box sx={{ border: '1px solid', borderColor: 'error.light', borderRadius: 1, p: 2 }}>
                <FormControlLabel
                  control={(
                    <Switch
                      color='error'
                      checked={Boolean(settings.enableDeleteAllSales)}
                      onChange={handleToggle('enableDeleteAllSales')}
                      disabled={loading || saving}
                    />
                  )}
                  label={<Typography fontWeight={700}>Enable Delete All Sales Button</Typography>}
                />
                <Typography variant='body2' color='text.secondary'>
                  Default off hai. Enable karne par Sales Report mein tamam sales permanently delete karne ka button nazar aayega.
                </Typography>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(settings.includeDeliveryAmountInCash)}
                      onChange={handleToggle('includeDeliveryAmountInCash')}
                      disabled={loading || saving}
                    />
                  )}
                  label={<Typography fontWeight={700}>Include Delivery Amount in Cash</Typography>}
                />
                <Typography variant='body2' color='text.secondary'>
                  Default off hai. Enable karne par non-Foodpanda delivery sales ka poora amount Sales Report ke cash total mein count hoga.
                </Typography>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <FormControlLabel
                  control={<Switch checked={Boolean(settings.allowDecimalPayments)} onChange={handleToggle('allowDecimalPayments')} disabled={loading || saving} />}
                  label={<Typography fontWeight={700}>Allow Decimal Payments</Typography>}
                />
                <Typography variant='body2' color='text.secondary'>
                  Enable honay par payment amount 2 decimal places tak accept hoga. Disable honay par sirf whole-number amount accept hoga.
                </Typography>
              </Box>

              {enabledCount === 0 && (
                <Alert severity="warning">
                  Kam az kam ek option enabled hona chahiye. Save par Takeaway automatic enable ho jayega.
                </Alert>
              )}

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setSettings((prev) => ({
                    ...prev,
                    enableDineIn: true,
                    enableTakeaway: true,
                    enableDelivery: true,
                    enableFoodpanda: true,
                  }))}
                  disabled={loading || saving}
                >
                  Enable All
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>

      <Snackbar open={Boolean(message)} autoHideDuration={3500} onClose={() => setMessage('')}>
        <Alert onClose={() => setMessage('')} severity="info" sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </>
  );
}
